// AdversaryAPI utility - fetches available attack methods from the API

export interface AttackMethod {
	value: string;
	label: string;
	scope: string[];
}

export interface AdversaryAPIConfig {
	baseUrl: string;
	endpoint: string;
	attackMethods: AttackMethod[];
}

type AvailableAttack = {
	type: string;
	scope?: string[] | null;
};

type AdversaryAPIResponse = {
	available_attacks?: AvailableAttack[];
};

// Cache for attack methods config
let cachedConfig: AdversaryAPIConfig | null = null;

const API_BASE_URL = "https://adversary-api.deepscanlm.com";

/**
 * Fetches available attack methods from the AdversaryAPI
 * Results are cached to avoid repeated network calls
 */
export async function fetchAdversaryAPIConfig(): Promise<AdversaryAPIConfig> {
	// Return cached config if available
	if (cachedConfig) {
		return cachedConfig;
	}

	try {
		// Fetch available attack methods from the API endpoint
		const response = await fetch(`${API_BASE_URL}/attacks/available`);
		if (!response.ok) {
			throw new Error(`Failed to fetch attack methods: ${response.statusText}`);
		}

		const data: AdversaryAPIResponse = await response.json();

		// Extract attack methods from the response
		// Expected format: { available_attacks: Array<{ type: string, scope: string[] }>, total_count: number }
		const availableAttacks: AvailableAttack[] = data.available_attacks ?? [];
		const attackMethods: AttackMethod[] = availableAttacks.map((attack) => ({
			value: attack.type,
			label: formatAttackMethodLabel(attack.type),
			scope: attack.scope ?? [],
		}));

		// Use the /derivative-datasets endpoint
		const endpoint = "/derivative-datasets";

		cachedConfig = {
			baseUrl: API_BASE_URL,
			endpoint,
			attackMethods,
		};

		return cachedConfig;
	} catch (error) {
		console.error("Error fetching attack methods:", error);
		throw new Error("Failed to load attack methods. Please try again later.");
	}
}

/**
 * Formats an attack method enum value into a human-readable label
 */
function formatAttackMethodLabel(value: string): string {
	// Convert snake_case or SCREAMING_SNAKE_CASE to Title Case
	return value
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
}

/**
 * Gets just the attack methods without fetching full config
 */
export async function getAttackMethods(): Promise<AttackMethod[]> {
	const config = await fetchAdversaryAPIConfig();
	return config.attackMethods;
}

/**
 * Gets attack methods filtered by dataset kind
 */
export async function getAttackMethodsForDatasetKind(
	datasetKind: string,
): Promise<AttackMethod[]> {
	const config = await fetchAdversaryAPIConfig();
	return config.attackMethods.filter((method) =>
		method.scope.includes(datasetKind),
	);
}

/**
 * Clears the cached config (useful for testing or forcing refresh)
 */
export function clearCache(): void {
	cachedConfig = null;
}
